import axios from '@/axios'

const API_BASE = `${process.env.VUE_APP_API_BASE_URL}/api/alquileres`
const ENDPOINTS = {
  myRent: `${API_BASE}/contratos/mi-alquiler/`,
}

const state = {
  loading: false,
  error: null,
  contract: null,
  vinculacion: null,
  invoices: [],
  payments: [],
  adjustments: [],
  penalties: [],
  projection: null,
  documents: [],
  documentsLoading: false,
}

const mutations = {
  SET_LOADING(state, val) { state.loading = val },
  SET_ERROR(state, err) { state.error = err },
  SET_CONTRACT(state, payload) { state.contract = payload },
  SET_VINCULACION(state, payload) { state.vinculacion = payload },
  SET_INVOICES(state, payload) { state.invoices = payload || [] },
  SET_PAYMENTS(state, payload) { state.payments = payload || [] },
  SET_ADJUSTMENTS(state, payload) { state.adjustments = payload || [] },
  SET_PENALTIES(state, payload) { state.penalties = payload || [] },
  SET_PROJECTION(state, payload) { state.projection = payload },
  SET_DOCUMENTS(state, payload) { state.documents = payload || [] },
  SET_DOCUMENTS_LOADING(state, val) { state.documentsLoading = val },
  ADD_DOCUMENT(state, payload) { state.documents.unshift(payload) },
  REMOVE_DOCUMENT(state, docId) { state.documents = state.documents.filter(d => d.id !== docId) },
}

const actions = {
  async fetchMyRental({ commit }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const { data } = await axios.get(ENDPOINTS.myRent, { withCredentials: true })

      if (data.vinculacion) {
        commit('SET_VINCULACION', data.vinculacion)
      } else {
        commit('SET_VINCULACION', null)
      }

      if (data.contract) {
        const c = data.contract
        commit('SET_CONTRACT', {
          id: c.id,
          cliente_email: c.cliente_email,
          cliente_nombre: c.cliente_nombre,
          fecha_inicio: c.fecha_inicio,
          fecha_fin: c.fecha_fin,
          monto_base: c.monto_base,
          moneda: c.moneda,
          dia_vencimiento: c.dia_vencimiento,
          aplicar_mora: c.aplicar_mora,
          porcentaje_mora_mensual: c.porcentaje_mora_mensual,
          esquema_ajuste: c.esquema_ajuste,
          indice_referencia: c.indice_referencia,
          periodicidad_ajuste_meses: c.periodicidad_ajuste_meses,
          estado: c.estado,
          resumen: c.resumen,
          propiedad: c.propiedad,
          propiedad_titulo: c.propiedad_titulo,
          propiedad_ubicacion: c.propiedad_ubicacion,
          inmobiliaria_nombre: c.inmobiliaria_nombre,
          inmobiliaria_telefono: c.inmobiliaria_telefono,
        })
        commit('SET_INVOICES', c.facturas || [])
        commit('SET_PAYMENTS', c.pagos || [])
        commit('SET_ADJUSTMENTS', c.ajustes || [])
        commit('SET_PENALTIES', c.penalidades || [])
      } else {
        commit('SET_CONTRACT', null)
        commit('SET_INVOICES', [])
        commit('SET_PAYMENTS', [])
        commit('SET_ADJUSTMENTS', [])
        commit('SET_PENALTIES', [])
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        commit('SET_CONTRACT', null)
        commit('SET_VINCULACION', null)
        commit('SET_INVOICES', [])
        commit('SET_PAYMENTS', [])
        commit('SET_ADJUSTMENTS', [])
        commit('SET_PENALTIES', [])
        commit('SET_ERROR', null)
      } else {
        commit('SET_ERROR', err.response?.data || err.message || 'Error cargando datos de alquiler')
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async aceptarVinculacion({ commit }, contractId) {
    const { data } = await axios.post(
      `${API_BASE}/contratos/${contractId}/aceptar-vinculacion/`,
      {},
      { withCredentials: true }
    )
    commit('SET_VINCULACION', data)
    // Recargar para obtener el contrato completo
    await this.dispatch('rent/fetchMyRental')
  },

  async rechazarVinculacion({ commit }, contractId) {
    const { data } = await axios.post(
      `${API_BASE}/contratos/${contractId}/rechazar-vinculacion/`,
      {},
      { withCredentials: true }
    )
    commit('SET_VINCULACION', data)
  },

  async recalcInvoiceLateFees(_, invoiceId) {
    return axios.post(`${API_BASE}/facturas/${invoiceId}/recalcular-mora/`, {}, { withCredentials: true })
  },
  async confirmPayment(_, paymentId) {
    return axios.post(`${API_BASE}/pagos/${paymentId}/confirmar/`, {}, { withCredentials: true })
  },
  async generateInvoice({ state, dispatch }) {
    if (!state.contract?.id) throw new Error('Contrato no disponible')
    await axios.post(`${API_BASE}/contratos/${state.contract.id}/generar-factura/`, {}, { withCredentials: true })
    await dispatch('fetchMyRental')
  },
  async fetchDocuments({ state, commit }) {
    if (!state.contract?.id) return
    commit('SET_DOCUMENTS_LOADING', true)
    try {
      const { data } = await axios.get(`${API_BASE}/contratos/${state.contract.id}/documentos/`, { withCredentials: true })
      commit('SET_DOCUMENTS', data)
    } catch {
      commit('SET_DOCUMENTS', [])
    } finally {
      commit('SET_DOCUMENTS_LOADING', false)
    }
  },

  async uploadDocument({ state, commit, dispatch }, formData) {
    if (!state.contract?.id) throw new Error('Contrato no disponible')
    const { data } = await axios.post(`${API_BASE}/contratos/${state.contract.id}/documentos/`, formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    commit('ADD_DOCUMENT', data)
    return data
  },

  async deleteDocument({ commit }, { contractId, docId }) {
    await axios.delete(`${API_BASE}/contratos/${contractId}/documentos/${docId}/`, { withCredentials: true })
    commit('REMOVE_DOCUMENT', docId)
  },

  async fetchProjection({ state, commit }) {
    if (!state.contract?.id) throw new Error('Contrato no disponible')
    const { data } = await axios.get(`${API_BASE}/contratos/${state.contract.id}/proyeccion/`, { withCredentials: true })
    commit('SET_PROJECTION', data)
  },
}

const getters = {
  myContract: s => s.contract,
  myVinculacion: s => s.vinculacion,
  vinculacionStatus: s => s.vinculacion?.status || null,
  vinculacionActive: s => Boolean(s.vinculacion?.is_invitation_active),
  myInvoices: s => s.invoices,
  myPayments: s => s.payments,
  myAdjustments: s => s.adjustments,
  myPenalties: s => s.penalties,
  myProjection: s => s.projection,
  myDocuments: s => s.documents,
  documentsLoading: s => s.documentsLoading,
  rentLoading: s => s.loading,
  rentError: s => s.error,
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
